/**
 * Spec 032 (T018/19/21) "Fix B" — voice transcript data-channel contract.
 *
 * Voice turns are processed server-side (LiveKit agent → Cloud `/turn` →
 * `processTextMessage` → `runTurn`) and never flow through the frontend
 * text/SSE path, so the live thread showed nothing during a voice call. Rather
 * than poll the persisted transcript, the agent now publishes each spoken turn's
 * TEXT over the SAME reliable LiveKit data channel it already uses for widgets
 * and `status_label` — so voice text reaches the thread through the identical
 * content-block path text streams over SSE. Voice ⇄ text become symmetric: one
 * live channel per transport, no polling, no transcript merge.
 *
 * Envelope (matches the existing `{ event, data }` widget / status_label shape):
 *   { event: 'transcript', data: { role, text, turn_id?, final? } }
 *
 * Producer: `agent/src/transcript-publish.ts` (the agent is a separate build and
 * does NOT import from `shared/` at runtime — it duplicates the `'transcript'`
 * literal, exactly as it duplicates `VOICE_AGENT_NAME`). Consumer: the frontend
 * `useVoiceConversation` `RoomEvent.DataReceived` handler via
 * {@link parseVoiceTranscriptMessage}. Keep the two in sync.
 */

import { z } from 'zod';

/** Data-channel `event` discriminator for a spoken-transcript message. */
export const VOICE_TRANSCRIPT_EVENT = 'transcript' as const;

export const VoiceTranscriptMessageSchema = z.object({
  event: z.literal(VOICE_TRANSCRIPT_EVENT),
  data: z.object({
    /** Who spoke this turn. Maps 1:1 to the frontend `V2ConversationTurn.role`. */
    role: z.enum(['user', 'assistant']),
    /** The spoken text — the user's STT transcription or Anaka's `ttsText`. */
    text: z.string().min(1),
    /**
     * Optional server/agent turn id. Not required for the live channel (it is
     * the single live source for voice), reserved for future dedup against a
     * hydrate/poll path.
     */
    turn_id: z.string().optional(),
    /**
     * Reserved for future interim STT streaming. Absent / `true` = a settled
     * turn the consumer appends; `false` = an interim fragment the consumer
     * currently ignores (the agent MVP only ever emits final turns).
     */
    final: z.boolean().optional(),
  }),
});

export type VoiceTranscriptMessage = z.infer<typeof VoiceTranscriptMessageSchema>;
export type VoiceTranscriptData = VoiceTranscriptMessage['data'];

/**
 * Validate an already-JSON-parsed data-channel message and return its transcript
 * payload, or `null` when it is not a well-formed transcript message (a widget,
 * a status_label, or malformed input) — so the caller's widget path handles the
 * rest unchanged. Never throws.
 */
export function parseVoiceTranscriptMessage(parsed: unknown): VoiceTranscriptData | null {
  const result = VoiceTranscriptMessageSchema.safeParse(parsed);
  return result.success ? result.data.data : null;
}
